import React, { PureComponent, Fragment } from 'react';
import { hot } from 'react-hot-loader';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedDate } from 'react-intl';
import Skeleton from 'react-skeleton-loader';
import { Alert } from 'reactstrap';
import { get } from 'lodash';
import { DataTable, SendButton, DeleteButton } from '../';
import { esignProductTierNames } from '../../constants/esignProductTierNames';

class CompanyDocumentsTable extends PureComponent {
  static filteredRowExists(item, filter) {
    if (!item && !filter) {
      return false;
    }

    const values = Object.values(item).filter(val => val !== null && typeof val === 'string');
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < values.length; ++i) {
      if (values[i].toLowerCase().includes(filter.toLowerCase())) {
        return true;
      }
    }

    return false;
  }

  constructor(props) {
    super(props);

    this.state = {
      companyId: get(window, 'asure.appSettings.selectedCompany.companyId'),
    };
  }

  buildRows() {
    const {
      data,
      sendSignatureRequestsAction,
      docFilter,
      companyEsignTierName,
    } = this.props;
    const { companyId } = this.state;
    if (data.length === 0) {
      const skeletonCount = 30;
      const skeletonConfig = {
        width: '50%',
        borderRadius: '2px',
        color: '#ededed',
        widthRandomness: 0.2,
        height: '18px',
      };
      let i = 0;
      const skeletonLoaders = new Map();
      while (i < skeletonCount) {
        i += 1;
        skeletonLoaders.set(i, [
          {
            content: () => (<Fragment><Skeleton {...skeletonConfig} /><br /><Skeleton {...skeletonConfig} /></Fragment>),
          },
          {
            classes: ['d-flex', 'align-items-center'],
            content: () => (<Skeleton {...{ ...skeletonConfig, width: '100px' }} />),
          },
          {
            classes: ['d-sm-flex', 'd-none', 'align-items-center'],
            content: () => (<Skeleton {...{ ...skeletonConfig, width: '25px' }} />),
          },
          {
            classes: ['d-md-flex', 'd-none', 'align-items-center'],
            content: () => (<Skeleton {...{ ...skeletonConfig, width: '25px' }} />),
          },
          {
            classes: ['d-md-flex', 'd-none', 'align-items-center', 'justify-content-center'],
            content: () => (<Skeleton {...{ ...skeletonConfig, width: '16px', height: '20px' }} />),
          },
        ]);
      }
      return skeletonLoaders;
    }
    const isCompanySimpleSign = companyEsignTierName === esignProductTierNames.SIMPLESIGN;
    const rows = new Map();
    data.forEach((item) => {
      const {
        title,
        filename,
        category,
        id,
        uploadedBy,
        uploadDate,
        isPublishedToEmployee,
        isEsignatureDocument,
        existsInTaskList,
        isHelloSignTemplate,
      } = item;
      if (CompanyDocumentsTable.filteredRowExists(item, docFilter)) {
        rows.set(id, [
          {
            content: () => (
              <Fragment>
                <span className="text-primary">{title || filename}</span><br />
                <span className="text-muted">
                  {uploadDate && (
                    <Fragment>
                      <FormattedMessage id="UPLOADED" />
                      {' '}
                      <FormattedDate
                        value={uploadDate}
                        year="numeric"
                        month="long"
                        day="2-digit"
                      />
                      {' '}
                    </Fragment>
                  )}
                  {uploadedBy && (
                    <Fragment>
                      <FormattedMessage id={uploadDate ? 'BY' : 'UPLOADED_BY'} />
                      {' '}
                      {uploadedBy}
                    </Fragment>
                  )}
                </span>
              </Fragment>
            ),
          },
          {
            classes: ['d-flex', 'align-items-center'],
            content: () => <span>{category}</span>,
          },
          {
            classes: ['d-sm-flex', 'd-none', 'align-items-center'],
            content: () => <span className="text-capitalize"><FormattedMessage id={isEsignatureDocument ? 'YES' : 'NO'} /></span>,
          },
          {
            classes: ['d-md-flex', 'd-none', 'align-items-center'],
            content: () => <span className="text-capitalize"><FormattedMessage id={isPublishedToEmployee ? 'YES' : 'NO'} /></span>,
          },
          {
            classes: ['d-md-flex', 'd-none', 'align-items-center', 'justify-content-center'],
            content: () => (
              <div className="d-flex flex-row">
                <SendButton
                  id={id}
                  uniqueId="send-for-signature"
                  onClick={() => {
                    document.activeElement.blur();
                    sendSignatureRequestsAction(id);
                  }}
                  showButton={(isEsignatureDocument) && !(isHelloSignTemplate && isCompanySimpleSign)}
                  tooltipText="SEND_FOR_SIGNATURES"
                />
                <DeleteButton
                  id={id}
                  additionalInfo={{ companyId }}
                  uniqueId="delete-document"
                  outsideClickIgnoreClass="delete-tooltip"
                  deleteQuestion="DELETE_DOCUMENT_QUESTION"
                  message={existsInTaskList ? 'EXISTS_IN_TASK_LIST_WARNING' : undefined}
                />
              </div>
            ),
          },
        ]);
      }
    });
    return rows;
  }

  noResultsAlert = filter => (
    <Alert color="info">
      {<FormattedMessage id="NO_DOCUMENTS_MATCH" />} {` "${filter}".`}
    </Alert>
  )

  render() {
    const { rowAction, docFilter } = this.props;
    const rows = this.buildRows();
    const alert = (rows && rows.size) ||
      (this.props.filtering) ||
      (this.props.getEsignatureDocsLoading === 'loading')
      ? null
      : this.noResultsAlert(docFilter);
    return (
      <React.Fragment>
        <div className="company-documents-table">
          <DataTable
            headings={[
              { classes: ['border-0'], content: () => <FormattedMessage id="DOCUMENT" /> },
              { classes: ['border-0'], content: () => <FormattedMessage id="CATEGORY" /> },
              { classes: ['border-0', 'd-none', 'd-sm-block'], content: () => <FormattedMessage id="ESIGNATURE_REQUIRED" /> },
              { classes: ['border-0', 'd-none', 'd-md-block'], content: () => <FormattedMessage id="PUBLISHED_TO_EMPLOYEE" /> },
              { classes: ['border-0', 'd-none', 'd-md-block'], content: '' },
            ]}
            rows={rows}
            rowAction={rowAction}
            columnClasses={['col-6 col-sm-7 col-md-4', 'col-6 col-sm-3 col-md-3', 'col-sm-2 col-md-2', 'col-md-2', 'col-md-1']}
          />
        </div>
        {alert}
      </React.Fragment>
    );
  }
}

CompanyDocumentsTable.defaultProps = {
  docFilter: '',
  filtering: false,
  searching: false,
  getEsignatureDocsLoading: '',
  sendSignatureRequestsAction: () => {},
};

CompanyDocumentsTable.propTypes = {
  rowAction: PropTypes.func.isRequired,
  sendSignatureRequestsAction: PropTypes.func,
  getEsignatureDocsLoading: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.shape({
    esignDocs: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      filename: PropTypes.string,
      title: PropTypes.string,
      uploadDate: PropTypes.string,
      uploadedBy: PropTypes.string,
      category: PropTypes.string,
      isPublishedToEmployee: PropTypes.bool,
      isEsignatureDocument: PropTypes.bool,
      isHelloSignTemplate: PropTypes.bool,
      existsInTaskList: PropTypes.bool,
    })),
  })).isRequired,
  docFilter: PropTypes.string,
  filtering: PropTypes.bool,
  searching: PropTypes.bool,
  companyEsignTierName: PropTypes.string.isRequired,
};

export default hot(module)(CompanyDocumentsTable);



// WEBPACK FOOTER //
// ./src/components/CompanyDocumentsTable/CompanyDocumentsTable.jsx